import Backbone from 'backbone';
import _ from 'lodash';

import makeFormReadOnly from '@/util/makeFormReadOnly';
import EmptySetView from '@/views/EmptySetView';

import OrderAccess from '../../utils/OrderAccess';
import DesktopFormView from '../../forms/DevicesFormView/DesktopFormView/DesktopFormView';
import MFDFormView from '../../forms/DevicesFormView/MFDFormView/MFDFormView';
import SpecialtyRequirementFormView from '../../forms/DevicesFormView/SpecialtyRequirementFormView/SpecialtyRequirementFormView';
import MaintenanceFormView from '../../forms/DevicesFormView/MaintenanceFormView/MaintenanceFormView';
import WideFormatFormView from '../../forms/DevicesFormView/WideFormatFormView/WideFormatFormView';
import PRTNRFormView from '../../forms/DevicesFormView/PRTNRFormView/PRTNRFormView';
import RelocationFormView from '../../forms/DevicesFormView/RelocationFormView/RelocationFormView';
import PRTNRA3FormView from '../../forms/DevicesFormView/PRTNRA3FormView/PRTNRA3FormView';
import PRTNRA4FormView from '../../forms/DevicesFormView/PRTNRA4FormView/PRTNRA4FormView';
import PRTNRProdFormView from '../../forms/DevicesFormView/PRTNRProdFormView/PRTNRProdFormView';
import VB0FormView from '../../forms/DevicesFormView/VB0FormView/VB0FormView';
import VB0v3FormView from '../../forms/DevicesFormView/VB0v3FormView/VB0v3FormView';
import DefaultFormView from '../../forms/DevicesFormView/DefaultFormView/DefaultFormView';

import SubmittableOrderView from '../SubmittableOrderView/SubmittableOrderView';

import tmplOrderDevicesView from './OrderDevicesView.hbs';

import './OrderDevicesView.css';

const OrderDevicesView = SubmittableOrderView.extend({
  template: tmplOrderDevicesView,

  className: 'order-devices-view',

  ui: {
    form: '.form-container'
  },

  regions: {
    form: '@ui.form'
  },

  childViewEvents: {
    'has:errors': 'onHasErrors',
    'no:errors': 'onNoErrors'
  },

  async onAttach() {
    if (this.model.get('orderDetails').showValidation) {
      const calledOnSubmit = this.model.get('orderDetails').showValidation;
      await this.validate({ calledOnSubmit });
    }
  },

  onRender() {
    const lineItems = this.model.get('lineItems');
    const orderItems = this.model.get('orderItems');
    const existingFormData = this.model.get('formData');
    const orderDetails = this.model.get('orderDetails');
    const totals = this.model.get('totals');
    const deviceMatrix = this.model.get('deviceMatrix');

    switch (this.model.get('orderDetails').orderTypeId) {
      case '1':
      case '3':
        this.showChildView(
          'form',
          new MFDFormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails,
              orderItems
            })
          })
        );
        break;

      case '2':
        this.showChildView(
          'form',
          new PRTNRFormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails
            })
          })
        );
        break;

      case '4':
        this.showChildView(
          'form',
          new MaintenanceFormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails
            })
          })
        );
        break;

      case '5':
      case '17':
        this.showChildView(
          'form',
          new WideFormatFormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails,
              orderItems
            })
          })
        );
        break;

      case '6':
      case '10':
      case '11':
        this.showChildView(
          'form',
          new DesktopFormView({
            model: new Backbone.Model({
              lineItems,
              orderItems,
              existingFormData,
              orderDetails,
              totals
            })
          })
        );
        break;

      case '12':
        this.showChildView(
          'form',
          new RelocationFormView({
            model: new Backbone.Model({
              orderDetails,
              orderItems
            })
          })
        );
        break;

      case '13':
        this.showChildView(
          'form',
          new PRTNRA3FormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails
            })
          })
        );
        break;

      case '14':
        this.showChildView(
          'form',
          new PRTNRA4FormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails
            })
          })
        );
        break;

      case '15':
        this.showChildView(
          'form',
          new PRTNRProdFormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails
            })
          })
        );
        break;

      case '16':
      case '18':
        this.showChildView(
          'form',
          new VB0FormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails,
              deviceMatrix
            })
          })
        );
        break;

      case '20':
      case '21':
        this.showChildView(
          'form',
          new VB0v3FormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails,
              deviceMatrix
            })
          })
        );
        break;

      case '22':
      case '23':
      case '24':
      case '25':
      case '26':
        this.showChildView(
          'form',
          new DefaultFormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails
            })
          })
        );
        break;
      case '19':
        this.showChildView(
          'form',
          new SpecialtyRequirementFormView({
            model: new Backbone.Model({
              lineItems,
              orderDetails,
              orderItems,
              isPriceVisible: OrderAccess.isPriceVisible(orderDetails.orderDetails),
              isPriceEditable: OrderAccess.isPriceEditable(orderDetails.orderDetails)
            })
          })
        );
        break;

      default:
        this.showChildView(
          'form',
          new EmptySetView({
            emptyText: 'Device data not available for migrated orders.'
          })
        );
        break;
    }

    if (orderDetails.isReadOnly) {
      makeFormReadOnly(this.$el);
      this.$el.find('.devices-add-section').hide(); //hide MFD devices select inputs
      this.$el.find('.editDevice .btn').prop('disabled', false); //allows MFD View modal to open
    }
  },

  async onSave(formData) {
    const view = this.getChildView('form');
    return await view.onSave(formData);
  },

  onHasErrors() {
    this.trigger('has:errors');
  },

  onNoErrors() {
    this.trigger('no:errors');
  },

  async validate({ calledOnSubmit } = {}) {
    if (_.isFunction(this.getChildView('form').validate)) {
      const isValid = await this.getChildView('form').validate(calledOnSubmit);
      if (!isValid) this.showSubmissionError(this.model.get('orderDetails')?.orderId);
      return isValid !== false;
    }
    return true;
  }
});

export default OrderDevicesView;
